import type { Observable } from 'rxjs';
import type {
  AysClinicSnSearchItem,
  AysClinicSnSearchPage,
  AysClinicSnSummary,
} from './clinic.model';
import type { AysClinicSnIdentifier } from './clinic-sn-identifier';

/**
 * Country-neutral, read-only clinic contract used as an Angular DI token.
 *
 * This abstract class is the shared surface every clinic-consuming feature is
 * meant to depend on, decoupling consumers from a concrete implementation and
 * from the country/API-generation axis (DE-legacy, CH, SN-native).
 *
 * Deliberately minimal: only `get` / `search` / `getAll`.
 * `searchByDistance` was removed once its only two consumers (competitor radius
 * mode, scenario-analysis) moved to `AysClinicSnService.byDistance` directly —
 * both are meta-app-only, so a country-neutral seam bought them nothing
 * (BDA-5809 step 2). The concrete legacy/CH implementations still have the
 * method; their retirement belongs to step 4.
 *
 * It carries NO transformer parameters, NO DE-only extras (`cases`,
 * `caseNumbers`, `availableClinicLevels`, `availableSpecialEquipment`,
 * `handDisinfectionUsage`) and NO CH-only fields.
 * A method may only be added here if every country backend can serve it.
 *
 * ## Identity: opaque clinicSn, not (ik, sn)
 *
 * Every method speaks {@link AysClinicSnIdentifier} — an opaque `clinicSn` plus
 * an optional report `year`. `ik` rides along on the identifier for display but
 * is NOT part of identity (BDA-5809 step 3).
 *
 * **Round-trip invariant.** Each app binds exactly one implementation, so the
 * clinicSns a consumer hands back to `get`/`getAll` are ones that same
 * implementation minted in `search`/`getAll`. This matters because the two legacy
 * backends have to recover their own native ids from the opaque string:
 *
 * - DE-legacy (`AysClinicService`) needs `ik`/`sn` for `by-id/{ik}/{sn}`. It
 *   recovers them from the synthetic `L-{ik}-{sn}` form it mints, falling back to
 *   `ref.ik` when the clinicSn carries no institution key.
 * - CH (`ChClinicReadService`) encodes its `uid`/`sn` pair into its own opaque
 *   form and decodes it back; that codec is private to `clinic-ch-lib`.
 *
 * An iNEK Standortnummer is NOT reversible to an `(ik, sn)` pair — only the
 * SN-native backend can resolve one, and it is the only implementation that ever
 * emits one. Swiss and German ids are separate systems and are never mixed, so
 * the invariant holds by construction for the country axis; within DE it holds
 * because a single app binds a single implementation.
 *
 * `get()` returns the lean {@link AysClinicSnSummary} (`id`/`name`/`address`),
 * the only shape neutral consumers rely on. Concrete implementations may return
 * a richer subtype; code that needs the extra fields injects that concrete
 * service, not this contract — CH screens use `ChClinicReadService.getClinic()`
 * for the rich `HtmClinic`.
 *
 * The class is intentionally left undecorated (no `@Injectable`) so this domain
 * library stays framework-independent. The root-default binding
 * (`{ provide: ClinicReadService, useExisting: AysClinicService }`) is
 * registered in `provideAppCore`'s shared root providers (so it reaches every
 * app regardless of auth mode); only apps with a diverging backend (e.g. CH)
 * override it via their app-local `CLINIC_PROVIDER`.
 */
export abstract class ClinicReadService {
  abstract get(ref: AysClinicSnIdentifier): Observable<AysClinicSnSummary>;

  /**
   * Search clinics by term, returning one page plus the server-side match count.
   *
   * Returns {@link AysClinicSnSearchPage} rather than a bare array because the
   * result may be bounded: the SN-native backend paginates, so an array alone
   * would silently present the first page as the whole answer. Implementations
   * over unpaginated backends report `total === items.length` — see the type's
   * doc. No page/size parameter: two of the three backends cannot honour one,
   * and this contract only carries what every country can serve.
   */
  abstract search(q: string): Observable<AysClinicSnSearchPage>;

  abstract getAll(
    refs: AysClinicSnIdentifier[],
  ): Observable<AysClinicSnSearchItem[]>;
}
